/**
 * byo-v4-submit.js — the Giftship cart write (P6).
 *
 * The payload is copied from the stock builder as observed in the spike and
 * written down in `docs/byo-builder/GIFTSHIP-CONTRACT.md` ("The cart write").
 * GIFTSHIP-API.md §5 describes the same thing in prose; where the two read
 * differently the contract wins, because it is what the real builder sent.
 *
 * **One box is ONE cart line.** The line is the £10 base variant; the chosen
 * products ride on it as `_gs_bundle_ids[]` and friends, and Giftship's cart
 * transform (`gid://shopify/CartTransform/78020981`) expands it on the order.
 * Do not "fix" this into a line per component — that is a different cart.
 *
 * Property names are never invented here. The `_gs_*` scalars are fixed; the
 * customer-facing keys (the wrap step, Photo, Song, Message …) are the titles
 * Giftship's own steps return, so `buildProperties` takes the parsed builder
 * and only falls back to the contract's observed names when it has none.
 *
 * Two rules that cost real money when broken, both from the contract:
 *
 * 1. **Never dispatch the theme's `shapes:cart:*` events** to refresh a drawer.
 *    The theme re-dispatches `input` on every quantity field when it handles
 *    them, which submits quantity changes — it deleted a bundle in testing.
 *    To show the cart, navigate to `/cart`.
 * 2. The bundle key is **stable for the life of the box**, so an edit updates
 *    in place instead of adding a second bundle (OPEN-QUESTIONS.md #5).
 *
 * ES2020 module, no view layer.
 */

/** A bundle key. `crypto.randomUUID` where it exists, else the same shape. */
export function newBundleKey() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * `_gs_bundle_contents` reads "<product title> - Quantity: <n>", or
 * "<title> - <variant title> - Quantity: <n>" when the variant is not
 * "Default Title". Our products carry no variant title, so the short form is
 * the right one. "Quantity" is Giftship's own `quantity_text` translation.
 */
export function bundleContent(product, qty) {
  return `${product.title} - Quantity: ${qty}`;
}

/**
 * One `_byo_items` entry, for the order confirmation email
 * (emails/HANDOFF-byo-items.md, Claude Design, 25 Sep 2026). A multi-address
 * order's Giftship parent keeps one placeholder line per box with its
 * properties but no product objects, so the email has no photos to draw
 * without this. The shape is `_gs_bundle_contents` plus the photo:
 *   "<title> - Quantity: <n> - Image: <featured image, 160px wide>"
 * With no image, the " - Image:" part is left out and the email draws a cream
 * tile. Straight double quotes in a title become curly ones, so an entry can
 * never hold the `","` the email splits entries on, nor a JSON escape it would
 * print.
 */
export function byoItem(product, qty) {
  const title = String(product.title || '')
    .replace(/"(?=\S)/g, '\u201c')
    .replace(/"/g, '\u201d');
  const entry = `${title} - Quantity: ${qty}`;
  const img = product.img;
  if (!img) return entry;
  const url = /[?&]width=\d+/.test(img)
    ? img.replace(/([?&])width=\d+/, '$1width=160')
    : `${img}${img.includes('?') ? '&' : '?'}width=160`;
  return `${entry} - Image: ${url}`;
}

/**
 * The customer-facing property keys. Giftship's step titles are authoritative
 * and carry their own trailing spaces ("2. Choose Your Wrap "), so they are
 * used verbatim when the builder has been parsed.
 */
export function propertyKeys(gs) {
  const fields = (gs && gs.formFields) || [];
  const byWord = (word) => {
    const f = fields.find((x) => new RegExp(word, 'i').test(x.title || x.name || ''));
    return f ? f.title || f.name : null;
  };
  return {
    /* The image step's own title is the property key -- live orders carry
       "2. Choose Your Wrap " because that is what 2057's step is called.
       `wrapStepTitle` was read here and set nowhere: parseSteps returns
       `stepTitles`, so this was always the literal below, whatever the builder
       said. Harmless against 2057, which happens to match it; wrong against
       2484, whose step is "Choose Your Wrap " with no number. */
    wrap: (gs && gs.stepTitles && gs.stepTitles.image) || '2. Choose Your Wrap ',
    photo: byWord('photo') || 'Photo',
    song: byWord('song') || 'Song',
    message: byWord('message') || 'Message',
    from: byWord('from') || 'From',
    /* "keep wrapt until" is builder 2484's name for this field and contains
       neither "date" nor "open". Keep this in step with TOUCH_RE.date. */
    date: byWord('date|open|keep\\s*wrapt?\\s*until') || 'Open on',
    /* 2484 names this field "For", which "recipient|who" does not match: the
       fallback below then produced the right key only by coincidence, and
       would have missed a name carrying a trailing space the way "From " does.
       Anchored, so it cannot claim a field like "Information". */
    recipient: byWord('recipient|who|^\\s*for\\s*$') || 'For',
    /* D70: how it is given, when it is given in person. Before, that was only
       ever said by the keepsake wrap; now a smaller box can go in person
       without a keepsake box, in an outer mailer, and the order must say so. */
    inPerson: byWord('in\\s*person') || 'Gift In Person',
  };
}

/**
 * How the box is given, as Giftship's own form field — D72. Alan added a radio
 * for "Gift in person" or "Send directly to them" to the builder, so that it
 * prints on Giftship's packing form (OPEN-QUESTIONS #50). Found by its
 * options (one says "in person", another "direct", "send" or "them") or, failing
 * that, by its title, so its exact wording can change in Giftship without
 * touching code. Null when the builder has no such field yet.
 */
export function givingField(gs) {
  const fields = (gs && gs.formFields) || [];
  const person = (c) => /in\s*person/i.test(c);
  const direct = (c) => /direct|send|them|post/i.test(c);
  const f =
    fields.find((x) => (x.choices || []).some(person) && (x.choices || []).some(direct)) ||
    fields.find((x) => /in\s*person|how.*giv|giving|gift(ing)?\s*method/i.test(x.title || x.name || ''));
  if (!f) return null;
  const choices = f.choices || [];
  return {
    key: f.title || f.name,
    person: choices.find(person) || 'Gift In Person',
    direct: choices.find(direct) || 'Send Directly To Them',
  };
}

/**
 * Build the properties for one box. Returned as a plain object of
 * `{ scalars, arrays }` so it can be asserted on without a cart.
 */
export function buildProperties({ b, config, gs, bundleKey }) {
  const box = b.box;
  const keys = propertyKeys(gs);

  const arrays = {
    _gs_bundle_ids: [],
    _gs_bundle_quantities: [],
    _gs_bundle_prices: [],
    _gs_bundle_contents: [],
  };

  const byoItems = [];
  box.items.forEach((it) => {
    const p = b.byId[it.id];
    if (!p) return;
    byoItems.push(byoItem(p, it.qty));
    arrays._gs_bundle_ids.push(String(p.variantId));
    arrays._gs_bundle_quantities.push(String(it.qty));
    /* GIFTSHIP-CONTRACT.md records this as "variant price / 100 × qty", i.e.
       major units. The live cart transform disagrees: sending ["28","2.5","6",
       "3.5"] for a £40 box priced the line at £10.41 — £10 base plus 41p,
       which is 28+3+6+4, each value rounded and taken as PENCE. Minor units
       it is. See PROGRESS.md, 21 Sep. */
    arrays._gs_bundle_prices.push(String(p.price * it.qty));
    arrays._gs_bundle_contents.push(bundleContent(p, it.qty));
  });

  /* The paid touches are Giftship "product" extras — GIFTSHIP-CONTRACT.md's
     "3. Add Extras" step lists Printed Keepsake Photo and Song QR Code, both
     £2.50 — so they ride as bundle components like anything else in the box.
     Without them the transform priced base + items only, and a box carrying a
     photo and a song was charged £5.00 less than Confirm had just quoted.
     Found by P7's exit criterion that the cart must agree with Confirm.

     A touch is only charged when the merchant has picked its product in the
     section settings. `missingExtras` names any that could not be, so the gap
     is reported rather than quietly discounted. */
  const extras = config.extras || {};
  const missingExtras = [];
  const addExtra = (on, key, label) => {
    if (!on) return;
    const x = extras[key] || {};
    const price = typeof x.price === 'number' ? x.price : 0;
    if (!price) return; /* free by configuration — the keepsake box */
    if (!x.variantId) {
      missingExtras.push(key);
      return;
    }
    arrays._gs_bundle_ids.push(String(x.variantId));
    arrays._gs_bundle_quantities.push('1');
    arrays._gs_bundle_prices.push(String(price));
    arrays._gs_bundle_contents.push(bundleContent({ title: label }, 1));
  };
  addExtra(box.photo.on, 'photo', 'Printed Keepsake Photo');
  addExtra(box.song.on && !!box.song.text, 'song', 'Song QR Code');

  const scalars = {
    _gs_bundle_key: bundleKey,
    _gs_bundle_discount: '0',
    _gs_bundle_currency: (config.currency || 'GBP').toUpperCase(),
    _gs_bundle_rate: '1.0',
  };

  /* Products only, in box order: the box itself and the paid touches are
     shown separately by the email. A JSON array written as one string, so it
     keeps exactly this shape on the cart line, through Giftship's hand-off
     and on to the order. `_gs_bundle_contents` and "In the box" stay as the
     text fallback. */
  if (byoItems.length) scalars._byo_items = JSON.stringify(byoItems);

  /* The image and form values, which Giftship also wants as one JSON string
     because Wrapt's `handle_bundle_properties` setting is on. */
  const item = {};

  if (box.keepsake && box.wrap) {
    arrays[keys.wrap] = [box.wrap];
    item[keys.wrap] = [box.wrap];
  }
  if (box.photo.on && box.photo.file && box.photo.file.url) {
    scalars[keys.photo] = box.photo.file.url;
    item[keys.photo] = box.photo.file.url;
  }
  if (box.song.on && box.song.text) {
    scalars[keys.song] = box.song.text;
    item[keys.song] = box.song.text;
  }
  if (box.messageOn && box.message) {
    scalars[keys.message] = box.message;
    item[keys.message] = box.message;
    if (box.from) {
      scalars[keys.from] = box.from;
      item[keys.from] = box.from;
    }
  }
  if (box.date.on && box.date.value) {
    scalars[keys.date] = box.date.value;
    item[keys.date] = box.date.value;
  }
  if (box.recipient) {
    scalars[keys.recipient] = box.recipient;
    item[keys.recipient] = box.recipient;
  }
  /* How it is given (D72). Giftship's own radio when the builder has one,
     written for both routes so the packing form always says which; otherwise
     the plain line note from D70, written only when it is in person. The
     private key is ours: the cart and edit mode read it back whatever the
     radio is called, and it is also what marks a line as a BYO box rather
     than a ready-made one (byo-v4-cart.js isReadyMade), so it is always set.
     D97: the builder no longer asks, so a new box is always "direct"; only a
     box from before, opened with Edit, can still be in person. */
  const giving = givingField(gs);
  if (giving) {
    const value = box.inPerson ? giving.person : giving.direct;
    scalars[giving.key] = value;
    item[giving.key] = value;
  } else if (box.inPerson) {
    const given = box.keepsake && box.wrap ? 'Yes · keepsake box in the mailer' : 'Yes · in an outer mailer';
    scalars[keys.inPerson] = given;
    item[keys.inPerson] = given;
  }
  scalars._byo_given = box.inPerson ? 'in person' : 'direct';

  scalars._gs_item_properties = JSON.stringify(item);

  if (missingExtras.length) {
    console.warn('[byo-v4] charged nothing for:', missingExtras.join(', '),
      '— pick the product in the builder section settings.');
  }

  return { scalars, arrays, missingExtras };
}

/**
 * The multipart body the contract specifies: scalars as `properties[key]`,
 * arrays as repeated `properties[key][]`.
 */
export function buildForm({ baseVariantId, scalars, arrays, quantity = 1 }) {
  const fd = new FormData();
  fd.append('id', String(baseVariantId));
  fd.append('quantity', String(quantity));

  Object.keys(scalars).forEach((k) => fd.append(`properties[${k}]`, scalars[k]));
  Object.keys(arrays).forEach((k) => arrays[k].forEach((v) => fd.append(`properties[${k}][]`, v)));

  return fd;
}

/**
 * Which product a stock rejection was about. The cart transform enforces
 * component stock, so the message names the component, not the base.
 */
export function blameProduct(message, b) {
  if (!message) return null;
  const items = b.box.items.map((it) => b.byId[it.id]).filter(Boolean);
  return items.find((p) => p && p.title && message.includes(p.title)) || null;
}

/**
 * Add the box to the cart. Resolves `{ ok: true, bundleKey }`, or
 * `{ ok: false, reason, product }` — never throws for a rejection, because a
 * rejection is a real state the UI draws (GIFTSHIP-API.md §5).
 */
export async function submitBox({ b, config, gs, bundleKey }) {
  const baseVariantId = config.baseVariantId;

  /* The £10 base is what the whole bundle rides on. Without it there is no
     line to hang the properties from, and Shopify rejects an unavailable
     variant outright — which is the state while `new-byo` is still a draft. */
  if (!baseVariantId) {
    return {
      ok: false,
      reason: 'no-base',
      message: 'The box product is not published yet, so nothing can be added to the cart.',
    };
  }

  const key = bundleKey || newBundleKey();
  const { scalars, arrays } = buildProperties({ b, config, gs, bundleKey: key });
  const body = buildForm({ baseVariantId, scalars, arrays });

  let res;
  try {
    res = await fetch(`${config.routes.cartAdd || '/cart/add'}`, {
      method: 'POST',
      headers: { Accept: 'application/javascript' },
      body,
    });
  } catch (e) {
    return { ok: false, reason: 'network', message: 'The cart did not answer. Check your connection and try again.' };
  }

  if (res.ok) return { ok: true, bundleKey: key };

  let message = '';
  try {
    const json = await res.json();
    message = json.description || json.message || '';
  } catch {
    message = '';
  }

  return { ok: false, reason: 'rejected', message, product: blameProduct(message, b) };
}

/** The live cart, or an empty shell if it cannot be read. */
export async function loadCart() {
  try {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) return { items: [] };
    return await res.json();
  } catch {
    return { items: [] };
  }
}

/** Every line this build wrote, in cart order — one line per bundle. A
    ready-made box sent with its extras is a bundle too, but not a box this
    builder lettered (D91), so it is left out: `_rm_key` marks it. */
export function bundleLines(cart) {
  return (cart.items || []).filter((i) => i.properties && i.properties._gs_bundle_key && !i.properties._rm_key);
}

/** The line carrying a given bundle key, with its position among bundles. */
export function findBundleLine(cart, bundleKey) {
  const lines = bundleLines(cart);
  const index = lines.findIndex((i) => i.properties._gs_bundle_key === bundleKey);
  return index === -1 ? null : { line: lines[index], index };
}

/**
 * "Box A" once there is more than one, else "This Box" (byo-stages-v3.jsx:3,
 * renamed from "Bundle" by Alan, D54). Lettered in the order the boxes were
 * built, as the cart letters them (groupCart): cart.js lists the newest first.
 */
export function bundleName(cart, bundleKey) {
  const found = findBundleLine(cart, bundleKey);
  const count = bundleLines(cart).length;
  if (!found || count < 2) return 'This Box';
  return `Box ${String.fromCharCode(65 + (count - 1 - found.index))}`;
}

/**
 * Update a bundle in place.
 *
 * One box is one line, so an edit is one `/cart/change.js` on that line rather
 * than a remove-and-re-add: re-adding would put the box at the end of the cart
 * and lose its position, and a failed re-add would lose the box entirely.
 * The line is found by `_gs_bundle_key`, which is why that key is stable for
 * the life of the box (OPEN-QUESTIONS.md #5). Every other line is untouched.
 */
export async function updateBox({ b, config, gs, bundleKey }) {
  if (!bundleKey) return { ok: false, reason: 'no-key', message: 'That box is not in the cart.' };

  const cart = await loadCart();
  const found = findBundleLine(cart, bundleKey);
  if (!found) {
    return { ok: false, reason: 'gone', message: 'That box is no longer in your cart.' };
  }

  const { scalars, arrays } = buildProperties({ b, config, gs, bundleKey });

  /* change.js takes properties as an object; the repeated-array form is only
     for the multipart add. Arrays go through as arrays. */
  const properties = { ...scalars };
  Object.keys(arrays).forEach((k) => {
    properties[k] = arrays[k];
  });

  let res;
  try {
    res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: found.line.key, quantity: 1, properties }),
    });
  } catch {
    return { ok: false, reason: 'network', message: 'The cart did not answer. Check your connection and try again.' };
  }

  if (res.ok) return { ok: true, bundleKey };

  let message = '';
  try {
    const json = await res.json();
    message = json.description || json.message || '';
  } catch {
    message = '';
  }
  return { ok: false, reason: 'rejected', message, product: blameProduct(message, b) };
}

/**
 * Refresh the theme's cart count without touching its internals.
 *
 * GIFTSHIP-CONTRACT.md is explicit that dispatching the theme's own
 * `shapes:cart:*` events makes it re-submit every quantity field, which
 * deleted a bundle in testing. So this only reads `/cart.js` and writes the
 * number into whatever bubble the header already renders.
 */
/**
 * Put the true number in the header bag.
 *
 * This theme's badge is Alpine: `sections/header.liquid` renders
 * `x-text="$store.cart_count.count"` inside `x-show="… > 0"`, and the store is
 * defined in global.bundle.js. Writing textContent at those nodes did nothing —
 * Alpine owns them and rewrites from the store, and the generic selectors this
 * used to try did not match this theme at all.
 *
 * The store's own updates come from `shapes:cart:*` events, and we must not
 * dispatch those: the theme answers them by re-dispatching `input` on every
 * quantity field, which submits quantity changes and destroyed a bundle in
 * testing (GIFTSHIP-CONTRACT.md). Setting `count` directly is reactive, touches
 * only the display, and asks the theme for nothing.
 */
export async function refreshCartCount() {
  const cart = await loadCart();
  const count = cart.item_count || 0;

  try {
    const store = window.Alpine && typeof window.Alpine.store === 'function' && window.Alpine.store('cart_count');
    if (store) store.count = count;
  } catch (_) {
    /* no Alpine on this page: the fallback below still runs */
  }
  /* The store reads this on boot, so a later navigation starts from the truth. */
  if (window.theme) window.theme.cartItemCount = count;

  /* Themes that render the badge as plain markup. */
  document
    .querySelectorAll('[data-cart-count], .cart-count, .header__cart-count, [data-header-cart-count]')
    .forEach((el) => {
      el.textContent = String(count);
      el.setAttribute('data-count', String(count));
      if (count > 0) el.removeAttribute('hidden');
    });

  return count;
}

window.BYOSubmit = { newBundleKey, buildProperties, buildForm, propertyKeys, bundleContent, blameProduct, submitBox, updateBox, loadCart, bundleLines, findBundleLine, bundleName, refreshCartCount };
export default window.BYOSubmit;

/* Published for the siblings that used to `import` this file. See the
   section's loader: relative imports drop the ?v= and are served from a
   year-long CDN cache, so a sibling could be a build behind. */
Object.assign((window.BYOV4 = window.BYOV4 || {}), { blameProduct, buildForm, buildProperties, bundleContent, bundleLines, bundleName, findBundleLine, loadCart, newBundleKey, propertyKeys, refreshCartCount, submitBox, updateBox });
